<template>
  <div class="max-w-6xl h-full m-auto py-4 px-4 flex flex-col space-y-4">
    <div class="flex flex-row justify-between space-x-4 py-2">
      <h2 class="text-2xl">Balance/Credit Due Report</h2>
      <div class="flex space-x-4 items-center">
        <span
          role="checkbox"
          :aria-checked="creditBalance"
          @click="creditBalance = !creditBalance"
          :class="creditBalance ? 'bg-green-600' : 'bg-red-600'"
          class="
            relative
            inline-block
            flex-shrink-0
            mt-1
            h-6
            w-11
            border-2 border-transparent
            rounded-full
            cursor-pointer
            transition-colors
            ease-in-out
            duration-200
            focus:outline-none focus:shadow-outline
          "
        >
          <span
            aria-hidden="true"
            :class="creditBalance ? 'translate-x-5' : 'translate-x-0'"
            class="
              inline-block
              h-5
              w-5
              rounded-full
              bg-white
              shadow
              transform
              transition
              ease-in-out
              duration-200
            "
          ></span>
        </span>
        <span
          class="
            inline-block
            items-center
            px-2
            py-0.5
            rounded-md
            text-sm
            font-medium
          "
          :class="[
            creditBalance
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800',
          ]"
        >
          {{ creditBalance ? "Credit" : "Balance" }}
        </span>

        <v-date-picker v-model="range" mode="range" color="green">
          <template v-slot="{ inputValue, inputEvents }">
            <input
              class="px-2 py-1 border rounded"
              style="min-width: 217px"
              :value="inputValue"
              v-on="inputEvents"
            />
          </template>
        </v-date-picker>

        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading || !gridHasData"
        >
          <span>Download CSV</span>
          <icon set="outline" name="document-download" class="h-5 w-5" />
        </Button>
        <Button
          v-on:click-handler="loadReport()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading"
        >
          <span>Refresh Report</span>
          <LoadingSpinner v-if="isLoading" class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
      :singleClickEdit="true"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import { ApiFactory } from "@/api/ApiFactory.js";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";
import { date } from "@/components/agGrid/cellRenderers";
import OrderLink from "./components/OrderLink";
import CustomerOrdersWebOrderLink from "../Customers/components/CustomerOrdersWebOrderLink";

const Reports = ApiFactory.get("reports");

var startRange = new Date();
startRange.setDate(startRange.getDate() - 30);

export default {
  name: "BalanceDueReport",
  components: {
    AgGridVue,
    Button,
    OrderLink, // eslint-disable-line
    CustomerOrdersWebOrderLink, // eslint-disable-line
  },
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      isLoading: false,
      error: null,
      creditBalance: false,
      range: {
        start: startRange,
        end: new Date(),
      },
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
  },
  beforeMount() {
    this.gridOptions = {};
    this.context = { componentParent: this };
    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      resizable: false,
    };

    this.columnDefs = [
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "CustomerOrdersWebOrderLink",
        cellRendererParams: {
          target: "_blank",
        },
        width: 100,
      },
      {
        headerName: "Name",
        field: "Name",
      },
      {
        headerName: "Order Date",
        field: "OrderDate",
        width: 110,
        cellRenderer: "dateCell",
        valueGetter: (params) => new Date(params.data.OrderDate).setHours(0,0,0,0),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true,
        },
      },
      {
        headerName: "ESD",
        field: "ExpectedShipDate",
        width: 110,
        cellRenderer: "dateCell",
        valueGetter: (params) => new Date(params.data.ExpectedShipDate).setHours(0,0,0,0),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true,
        },
      },
      {
        headerName: "Status",
        field: "Status",
      },
      {
        headerValueGetter: () => {
          return `${this.creditBalance ? "Credit" : "Balance"} Due`;
        },
        field: "BalanceDue",
        valueFormatter: (params) =>
          parseFloat(params.data.BalanceDue).toFixed(2),
      },
    ];

    this.frameworkComponents = {
      dateCell: date,
    };

    this.loadReport();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady(params) {
      params.api.sizeColumnsToFit();
    },
    exportCSV() {
      if (!this.gridHasData) return;
      this.gridOptions.api.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
      });
    },
    loadReport() {
      this.isLoading = true;
      this.gridOptions?.api?.showLoadingOverlay();

      Reports.getCreditBalanceDue({
        date_start: this.range.start.toISOString().substr(0, 10),
        date_end: this.range.end.toISOString().substr(0, 10),
        due: this.creditBalance ? "credit" : "balance",
      })
        .then((response) => {
          this.rowData = response.data.body;
          this.gridOptions?.api?.refreshHeader();
        })
        .finally(() => (this.isLoading = false));
    },
  },
  filters: {},
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
